import { Container, Sprite } from "pixi.js";
import type { CanvasPoint } from "@slot/game-shell";
import type { DesignRect, Log, PresentationTicker, ScreenShape } from "@slot/presentation";
import { RiveArtboard } from "../../rive/RiveArtboard";

/**
 * The chef's Rive file, as its project (`Metrospin/Github/Rive/candy-chef`, its `build/candy-chef.riv` copied to
 * `assets-src/game-shared-visuals/chef/`) builds it: the artboard 896 × 1200 (the art at half its master's size,
 * transparent), its default state machine, and the view model instance whose properties drive it — the triggers fire
 * a one-shot that interrupts whatever plays and returns to Idle (or to Spin while `isSpinning` holds); entry greets.
 */
export const CHEF_RIVE = {
  artboard: "Candy Chef",
  stateMachine: "Chef",
  viewModel: "CandyChef",
  viewModelInstance: "Default",
  artboardSize: { width: 896, height: 1200 },
  spinning: "isSpinning",
  /** The file's own hit area: 380 × 750 around (430, 500). */
  hitArea: { left: 240, top: 125, right: 620, bottom: 875 },
} as const;

/** How the chef is drawn (engine seconds). */
export interface ChefViewTuning {
  /** One drawn frame per this long: the state machine advances by the time since the last one. */
  frameSeconds: number;
}

/** A drawn frame is due a little early rather than a display frame late (two 60 Hz frames sum just under 1/30 s). */
const FRAME_TOLERANCE_SECONDS = 0.002;

/** The chef's reactions, the file's triggers. */
export type ChefReaction = "greet" | "win" | "bigWin";

/** What the chef's presenter drives. */
export interface ChefPort {
  setSpinning(spinning: boolean): void;
  react(reaction: ChefReaction): void;
  /** True when a canvas point falls on the chef as drawn. */
  hits(globalPoint: CanvasPoint): boolean;
}

/** Where the chef stands, in composition-root units: his artboard's top-left, the width it is drawn at, and how far down it the taps reach (artboard px). */
export interface ChefPlacement {
  x: number;
  y: number;
  width: number;
  hitBottom: number;
}

/**
 * Where the chef stands on each shape. Portrait follows the layout example, matched over his master art there (0.204
 * units per master px, the art's top-left at (605, 52) on its phone, where the composition is drawn at 0.879): the
 * artboard drawn 416 wide, its top-left 168 right of the cells' centre and 949 above it; the ladder bar covers him from
 * his jacket down (the artboard's 700, where taps stop) and the screen's edge cuts his right side. Desktop and landscape
 * have no example: he stands at the screen's bottom right, `screenHeight` of the screen tall, `sink` of his height below
 * its bottom edge (the art ends mid-thigh, the edge hides that: his jumps lift his legs up to 56 artboard px, a big win's
 * apex and its landing's squash, so 6 % keeps the cut under the edge), centred in the space right of the grid's frame
 * (`gap` clear of it) within the content area (in landscape the controls' band is outside it); he narrows to fit it.
 */
export const CHEF_GEOMETRY = {
  portrait: { x: 168, y: -949, width: 416, hitBottom: 700 },
  wide: { screenHeight: { desktop: 0.6, mobile_landscape: 0.72 }, sink: 0.06, gap: 24, fill: 0.95 },
} as const;

/**
 * The chef's placement for a layout: the shape, the viewport's design size, the content area and the composition root's
 * transform (both in the design-space root's frame, origin at the viewport's centre); null when there is no room.
 */
export function placeChef(input: { shape: ScreenShape; design: { width: number; height: number }; area: DesignRect; root: { x: number; y: number; scale: number }; frameHalfWidth: number }): ChefPlacement | null {
  if (input.shape === "mobile") return CHEF_GEOMETRY.portrait;
  const { screenHeight, sink, gap, fill } = CHEF_GEOMETRY.wide;
  const { area, root, design } = input;
  const { width: artboardWidth, height: artboardHeight } = CHEF_RIVE.artboardSize;
  const spaceLeft = root.x + (input.frameHalfWidth + gap) * root.scale;
  const space = area.right - spaceLeft;
  if (space <= 0) return null;
  const height = Math.min(screenHeight[input.shape] * design.height, (space * fill * artboardHeight) / artboardWidth);
  const width = (height * artboardWidth) / artboardHeight;
  const left = spaceLeft + (space - width) / 2;
  const top = design.height / 2 + sink * height - height;
  return { x: (left - root.x) / root.scale, y: (top - root.y) / root.scale, width: width / root.scale, hitBottom: CHEF_RIVE.hitArea.bottom };
}

/**
 * The chef: his Rive artboard drawn into a sprite behind the ladder bar, played on engine time (the presentation ticker,
 * paused and stepped with the dev tools) at the tuning's frame rate — a drawn frame is a render and a texture upload, the
 * state machine's advance is all but free. The file loads after boot; until it has, nothing shows and the presenter's
 * calls set what the chef will start from (his spinning) or pass (a reaction).
 */
export class ChefView extends Container implements ChefPort {
  private readonly sprite = new Sprite();
  private rive: RiveArtboard | null = null;
  private stopTicking: (() => void) | null = null;
  private spinning = false;
  private place: ChefPlacement | null = null;
  /** Device pixels per composition unit: the canvas the artboard draws into is the chef's size on the screen. */
  private pixelsPerUnit = 1;
  /** Engine seconds since the last drawn frame. */
  private pendingSeconds = 0;
  private disposed = false;

  public constructor(private readonly deps: { url: string | null; ticker: PresentationTicker; tuning: ChefViewTuning; log: Log }) {
    super({ label: "Chef" });
    this.addChild(this.sprite);
    this.visible = false;
    if (!deps.url) deps.log("[ChefView] no chef file");
    else void this.load(deps.url);
  }

  /** Where he stands (null hides him), drawn at `pixelsPerUnit` device pixels per unit. */
  public layout(place: ChefPlacement | null, pixelsPerUnit: number): void {
    this.place = place;
    if (place) this.position.set(place.x, place.y);
    this.pixelsPerUnit = pixelsPerUnit;
    this.applySize();
    this.visible = place !== null && this.rive !== null;
  }

  public setSpinning(spinning: boolean): void {
    this.spinning = spinning;
    this.rive?.setBoolean(CHEF_RIVE.spinning, spinning);
  }

  public react(reaction: ChefReaction): void {
    this.rive?.trigger(reaction);
  }

  public hits(globalPoint: CanvasPoint): boolean {
    if (!this.visible || !this.place) return false;
    const local = this.toLocal(globalPoint);
    const toArtboard = CHEF_RIVE.artboardSize.width / this.place.width;
    const x = local.x * toArtboard;
    const y = local.y * toArtboard;
    const area = CHEF_RIVE.hitArea;
    return x >= area.left && x <= area.right && y >= area.top && y <= Math.min(area.bottom, this.place.hitBottom);
  }

  public dispose(): void {
    this.disposed = true;
    this.stopTicking?.();
    this.rive?.dispose();
    this.destroy({ children: true });
  }

  private async load(url: string): Promise<void> {
    const { artboard, stateMachine, viewModel, viewModelInstance } = CHEF_RIVE;
    const rive = await RiveArtboard.load({ url, artboard, stateMachine, viewModel, viewModelInstance }, this.deps.log);
    if (!rive) return;
    if (this.disposed) {
      rive.dispose();
      return;
    }
    this.rive = rive;
    rive.setBoolean(CHEF_RIVE.spinning, this.spinning);
    this.sprite.texture = rive.texture;
    this.applySize();
    this.stopTicking = this.deps.ticker.add((deltaSeconds) => {
      if (!this.visible) return;
      this.pendingSeconds += deltaSeconds;
      if (this.pendingSeconds < this.deps.tuning.frameSeconds - FRAME_TOLERANCE_SECONDS) return;
      rive.render(this.pendingSeconds);
      this.pendingSeconds = 0;
    });
    this.visible = this.place !== null;
  }

  /** The canvas at the chef's size on the screen (never above the art's own), the sprite at his size in units. */
  private applySize(): void {
    if (!this.rive || !this.place) return;
    const { width, height } = CHEF_RIVE.artboardSize;
    const canvasWidth = Math.min(width, this.place.width * this.pixelsPerUnit);
    this.rive.resize(canvasWidth, (canvasWidth * height) / width);
    this.sprite.setSize(this.place.width, (this.place.width * height) / width);
  }
}
